import { useEffect, useState } from "react";
import { Badge, Card, DataState, InfoRow, PageHeader } from "@vms/ui-web";
import { useOrgContext } from "../../context/OrgContext";
import { useQuery } from "../../api/queries";

interface AgentRow {
  id: string;
  hubId: string;
  kind: string;
  name: string;
  version: string | null;
  protocolVersion: number | null;
  /** Native binary marketing version (mobile tablets only). */
  nativeVersion: string | null;
  /** Native build number (mobile tablets only). */
  buildNumber: string | null;
  status: string | null;
  lastSeenAt: string | null;
  updatedAt: string;
}

interface HubStatusRow {
  hubId: string;
  online: boolean;
  hubVersion: string | null;
  hubUiServerVersion: string | null;
  vmsUpdaterVersion: string | null;
  protocolVersion: number | null;
  lastSeenAt: string | null;
  updatedAt: string;
}

const REFRESH_MS = 30_000;

// Any non-offline / non-errored status counts as alive. The four agent kinds
// use different enums (SimulatorStatus has idle/running/loading/in-session,
// DisplayAgentStatus uses "connected", RegistrationTablet adds broadcasting
// and allocated, etc.) — easier to whitelist "dead" than to enumerate "alive".
function isAgentAlive(status: string | null): boolean {
  if (!status) return false;
  return status !== "offline" && status !== "errored";
}

function formatRelative(iso: string | null): string {
  if (!iso) return "never";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return "just now";
  if (ms < 60_000) return `${Math.max(1, Math.floor(ms / 1000))}s ago`;
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  return `${Math.floor(ms / 86_400_000)}d ago`;
}

export function VenueAudit() {
  const { currentVenue } = useOrgContext();
  const agents = useQuery<AgentRow[]>(
    currentVenue ? `/api/venues/${currentVenue.id}/agents` : null,
    { refetchIntervalMs: REFRESH_MS },
  );
  const hub = useQuery<HubStatusRow | null>(
    currentVenue ? `/api/venues/${currentVenue.id}/hub-status` : null,
    { refetchIntervalMs: REFRESH_MS },
  );

  // Re-render every 10s so "Last refreshed Xs ago" + "Last seen Xm ago" tick
  // forward between fetches. The fetches themselves stay on a 30s cadence.
  const [, setNow] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setNow((n) => n + 1), 10_000);
    return () => clearInterval(id);
  }, []);

  if (!currentVenue) return null;

  const grouped: Record<string, AgentRow[]> = {};
  for (const a of agents.data ?? []) {
    if (!grouped[a.kind]) grouped[a.kind] = [];
    grouped[a.kind].push(a);
  }
  // Sort each kind group: alive first, then offline; alphabetical within each bucket.
  for (const kind of Object.keys(grouped)) {
    grouped[kind].sort((a, b) => {
      const aliveA = isAgentAlive(a.status);
      const aliveB = isAgentAlive(b.status);
      if (aliveA !== aliveB) return aliveA ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Venue audit"
        subtitle="Versions of every component connected to this venue. Mirror of hub_status + hub_agents."
      />

      <DataState loading={hub.loading} error={hub.error}>
        <Card className="p-5 space-y-3">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-gray-900">Hub</span>
            {hub.data?.online ? (
              <Badge className="bg-green-100 text-green-700">Online</Badge>
            ) : (
              <Badge className="bg-amber-100 text-amber-700">Offline</Badge>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <InfoRow label="Venue ID" value={currentVenue.id} />
            <InfoRow label="Hub ID" value={hub.data?.hubId ?? "—"} />
            <InfoRow label="Hub version" value={hub.data?.hubVersion ?? "—"} />
            <InfoRow label="Hub-UI server" value={hub.data?.hubUiServerVersion ?? "—"} />
            <InfoRow label="VMS updater" value={hub.data?.vmsUpdaterVersion ?? "—"} />
            <InfoRow label="Protocol" value={String(hub.data?.protocolVersion ?? "—")} />
            <InfoRow
              label="Last seen"
              value={hub.data?.lastSeenAt ? new Date(hub.data.lastSeenAt).toLocaleString() : "Never"}
            />
          </div>
        </Card>
      </DataState>

      <DataState
        loading={agents.loading}
        error={agents.error}
        empty={!agents.loading && (agents.data ?? []).length === 0}
        emptyMessage="No agents reported yet."
      >
        <div className="space-y-4">
          {Object.entries(grouped).map(([kind, list]) => (
            <Card key={kind} className="overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                <span className="font-mono text-sm font-semibold text-gray-900">{kind}</span>
                <span className="text-xs text-gray-500">{list.length} instance{list.length === 1 ? "" : "s"}</span>
              </div>
              <ul className="divide-y divide-gray-100">
                {list.map((a) => {
                  const alive = isAgentAlive(a.status);
                  return (
                    <li
                      key={`${a.hubId}:${a.kind}:${a.id}`}
                      className={`px-5 py-3 flex items-center justify-between text-sm ${alive ? "" : "bg-gray-50"}`}
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`font-medium ${alive ? "text-gray-900" : "text-gray-500"}`}>{a.name}</span>
                          {alive ? (
                            <Badge className="bg-green-100 text-green-700">Online</Badge>
                          ) : (
                            <Badge className="bg-gray-200 text-gray-600">Offline</Badge>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          protocol {a.protocolVersion ?? "—"}
                          {a.nativeVersion
                            ? ` · binary ${a.nativeVersion}${
                                a.buildNumber ? ` (build ${a.buildNumber})` : ""
                              }`
                            : ""}{" "}
                          · last seen {formatRelative(a.lastSeenAt)}
                        </div>
                      </div>
                      <Badge className="bg-gray-100 text-gray-700">v{a.version ?? "?"}</Badge>
                    </li>
                  );
                })}
              </ul>
            </Card>
          ))}
        </div>
      </DataState>

      <div className="text-xs text-gray-400 text-right">
        Auto-refreshes every {REFRESH_MS / 1000}s · last refreshed {formatRelative(
          agents.lastFetchedAt ? new Date(agents.lastFetchedAt).toISOString() : null,
        )}
      </div>
    </div>
  );
}
